import axios from 'axios';

const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Flag to prevent redirect loops
let isRedirecting = false;

// Endpoints that should not trigger 401 redirect
const AUTH_CHECK_ENDPOINTS = ['/auth/me', '/auth/refresh', '/auth/login', '/auth/register'];

// Custom event to notify app of auth state changes
const dispatchAuthEvent = (type) => {
  window.dispatchEvent(new CustomEvent('auth-state-change', { detail: { type } }));
};

api.interceptors.response.use(
  response => response,
  async error => {
    const requestUrl = error.config?.url || '';

    if (error.response?.status === 401) {
      // Don't redirect for auth check endpoints (prevents loop)
      const isAuthEndpoint = AUTH_CHECK_ENDPOINTS.some(endpoint => requestUrl.includes(endpoint));

      if (!isAuthEndpoint && !isRedirecting) {
        isRedirecting = true;

        // Dispatch event to clear Recoil auth state
        dispatchAuthEvent('logout');

        // Small delay to allow state to clear
        setTimeout(() => {
          // Only redirect if not already on login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          isRedirecting = false;
        }, 100);
      }
    }

    return Promise.reject(error);
  }
);

// Export event helper for components to listen
export const onAuthStateChange = (callback) => {
  const handler = (e) => callback(e.detail.type);
  window.addEventListener('auth-state-change', handler);
  return () => window.removeEventListener('auth-state-change', handler);
};

export const authService = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh')
};

export const assessmentService = {
  getAll: () => api.get('/assessments'),
  getById: (id) => api.get(`/assessments/${id}`),
  getBySlug: (slug) => api.get(`/assessments/slug/${slug}`),
  create: (data) => api.post('/assessments', data),
  update: (id, data) => api.put(`/assessments/${id}`, data),
  delete: (id) => api.delete(`/assessments/${id}`),
  deleteAll: () => api.delete('/assessments/all'),
  calculate: (formData) => api.post('/assessments/calculate', { formData }),
  exportPDF: (id) => api.get(`/assessments/${id}/export/pdf`, { responseType: 'blob' }),
  exportCSV: (id) => api.get(`/assessments/${id}/export/csv`, { responseType: 'blob' })
};

export const adminService = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getStats: () => api.get('/admin/stats'),
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),
  getAssessments: () => api.get('/admin/assessments')
};

export default api;
